import { ActionTree, GetterTree, Module, MutationTree, } from 'vuex';
import {
  MathPart,
  MathPartTypes,
  Problem,
  ProblemSection,
  ProblemState,
  RootState,
  Solution,
  SolutionStep,
  StudentHistoryRecord,
  StudentSolution
} from '../types';
import { getMathServerClient, newGuid } from './util';
import { EquivalencyType, RandomProblem } from 'gradarius-client';


const mutations: MutationTree<ProblemState> = {

  problemGenerated(state, payload: Problem) {

    if (state.problem && state.studentSolution && !state.studentSolution.inHistory) {
      const s = state.studentSolution;
      const p = state.problem;
      state.history.push(createHistoryRecord(p, s));
    }

    state.problem = payload;
    const answersInternal: string[] = Array(state.problem.answersGrl.length).fill("");
    //const solution: MathPart[] = [];
    state.studentSolution = { answersInternal, checkAnswerCount: 0, hintRequested: false, solutionStepsViewed: 0, solved: undefined, id: newGuid(), inHistory: false };
  },

  hintRequested(state) {
    if (state.studentSolution) {
      state.studentSolution.hintRequested = true;
    }
  },

  openStep(state, num: number) {
    if (state.studentSolution && state.problem && !state.studentSolution.inHistory) {
      state.studentSolution.solutionStepsViewed = num;
    }
  },

  givedUp(state) {
    if (state.studentSolution && state.problem && !state.studentSolution.inHistory) {
      const sc = getSolutionStepCount(state);
      state.studentSolution.solutionStepsViewed = sc === undefined ? 0 : sc;
      state.studentSolution.solved = false;

      state.history.push(createHistoryRecord(state.problem, state.studentSolution));
    }
  },

  correctAnswerGiven(state) {
    if (state.studentSolution && state.problem && !state.studentSolution.inHistory) {
      state.studentSolution.checkAnswerCount++;
      state.studentSolution.solved = true;

      state.history.push(createHistoryRecord(state.problem, state.studentSolution));
    }
  },

  badAnswerGiven(state) {
    if (state.studentSolution) {
      state.studentSolution.checkAnswerCount++;
    }
  },

  // setStudentSolution(state, payload: MathPart[]) {
  //   if (state.studentSolution) {
  //     state.studentSolution.solution = payload;
  //   }
  // }

};

function createHistoryRecord(p: Problem, s: StudentSolution): StudentHistoryRecord {

  s.inHistory = true;

  const historyRecord: StudentHistoryRecord =
  {
    checkAnswerCount: s.checkAnswerCount, hintRequested: s.hintRequested,
    seed: p.seed, solutionStepsViewed: s.solutionStepsViewed,
    topicId: p.topicId, solved: !!s.solved, section: p.section
  };

  return historyRecord;
}

const actions: ActionTree<ProblemState, RootState> = {

  // async formatSolution({ commit, dispatch, state }, solution: MathPart[]) {
  //   if (state.studentSolution) {

  //     const client = getMathServerClient();
  //     //console.log("@@@"+JSON.stringify(solution));
  //     const formatted = await format(solution, "\u00A0\u00A0\u00A0\u00A0\u00A0", getMatharia(), client);
  //     // console.log("@@@"+JSON.stringify(formatted));
  //     commit("setStudentSolution", formatted);
  //   }
  // },

  async checkAnswers({ commit, dispatch, state }, grl: (string | undefined)[]): Promise<boolean> {
    if (state.problem) {

      try {
        if (state.problem.answersGrl.length !== grl.length) {
          commit("badAnswerGiven");
          return false;
        }

        for (const answer of state.problem.answersGrl) {
          for (let i = 0; i < grl.length; i++) {
            const grlToTry = grl[i] as string;

            const equal = await getMathServerClient().checkEquivalency(answer, grlToTry, EquivalencyType.calculus);
            if (equal) {
              grl.splice(i, 1);
              break;
            }
          }
        }

        if (grl.length === 0) {
          commit("correctAnswerGiven");
          return true;
        }
        else {
          commit("badAnswerGiven");
          return false;
        }
      }
      catch (e) {

        return false;
      }

    }
    return false;
  },

  async generateProblem({ commit, dispatch, state }, payload: { topicId: string, section: ProblemSection }) {
    try {
      const seed = Math.trunc(Math.random() * 100000000);

      const randomizedProblem = await getMathServerClient().generateRandomizedProblem(seed, payload.topicId);

      const statement = createProblemStatement(randomizedProblem);

      const answersGrl: string[] = [];
      for (const a of randomizedProblem.answer) {
        //FIXME: do not forget to remove
        console.log("Answer: " + a);
        answersGrl.push(a);
      }

      const solutions = createProblemSolutions(randomizedProblem);

      const problem: Problem = { statement, answersGrl, seed, solutions, topicId: payload.topicId, section: payload.section };

      commit("problemGenerated", problem);
    }
    catch (e) {
      const errorResponseData = e.response.data;
      return Promise.reject(errorResponseData.message);
    }


  },

  async requestHint({ commit, dispatch, state }) {
    commit("hintRequested");
  },


  async nextStep({ commit, dispatch, state }) {
    if (state.problem && state.studentSolution) {
      const stepCount = getSolutionStepCount(state);
      if (stepCount) {
        if (state.studentSolution.solutionStepsViewed+1 < stepCount) {
          //Open one more step
          commit("openStep", state.studentSolution.solutionStepsViewed + 1);
        }
        else {
          commit("givedUp");
        }
      }
    }
  },

  async giveUp({ commit, dispatch, state }) {
    if (state.problem && state.studentSolution) {
      commit("givedUp");
    }
  }

};

function createProblemStatement(randomizedProblem: RandomProblem): MathPart[] {
  const map = new Map<string, MathPart>();
  for (const p of randomizedProblem.statement.parameters) {
    const expression: MathPart = { t: MathPartTypes.grl, v: p.value };
    map.set(p.key, expression);
  }

  const template: string = (randomizedProblem.statement.template as string).trim();

  let inVar = template.startsWith('$');
  let buffer = "";
  const statement: MathPart[] = [];

  for (let i = 0; i < template.length; i++) {
    let s = template.charAt(i);
    const last = i === template.length - 1;

    if (inVar) {
      if (s === ' ' || s === '$' || last) {
        //var ends, text begins
        if (last) {
          buffer += s;
        }
        statement.push(map.get(buffer) as MathPart);
        buffer = "";
        inVar = (s === '$');
      }
      else {
        buffer += s;
      }
    }
    else {
      if (s === '$' || last) {
        //text ends, var begins
        if (last) {
          buffer += s;
        }

        statement.push({ t: MathPartTypes.text, v: buffer });
        buffer = "";
        inVar = true;
      }
      else {
        buffer += s;
      }
    }

  }

  return statement;
}

export function createProblemSolutions(randomizedProblem: RandomProblem): Solution[] {
  const solutions: Solution[] = [];
  for (const s of randomizedProblem.solutions) {
    const steps: SolutionStep[] = [];
    for (const step of s) {

      steps.push({ transitions: step.map((t: { expr: string; }) => t.expr), shift: calculateShift(step[0].expr, steps) });
    }
    solutions.push({ steps });
  }
  const before = JSON.stringify(solutions);
  fixMathServerBugs(solutions);
  const after = JSON.stringify(solutions);

  if (before !== after) {
    console.log("BEFORE: " + before);
    console.log("AFTER : " + after);
    console.log("STATEMENT : " + randomizedProblem.statement.template);
  }

  return solutions;

  function calculateShift(from: string, steps: SolutionStep[]): number {
    for (let i = steps.length - 1; i >= 0; i--) {
      const st = steps[i];
      const lastTransition = st.transitions[st.transitions.length - 1];
      if (from === lastTransition) {
        return st.shift;
      }
      if (lastTransition.indexOf(from) >= 0) {
        return st.shift + 1;
      }
    }
    return 0;
  }

  function fixMathServerBugs(solutions: Solution[]) {
    // for (const solution of solutions) {
    //   let steps: SolutionStep[] = [];
    //   for (const step of solution.steps) {
    //     const lastStep = steps.length > 0 ? steps[steps.length - 1] : null;
    //     if (lastStep && lastStep.transitions.length === 1 && step.transitions.length === 1) {
    //       lastStep.transitions.push(step.transitions[0]);
    //     }
    //     else {
    //       steps.push(step);
    //     }
    //   }
    //   solution.steps = steps;
    // }
    //У интегралов решение часто начинается с однотрнзитных утверждений вида u=bla bla, v = bla bla
    //Причем некоторые утверждения иногда дублируются. Порядок утверждений может быть любым
    for (const solution of solutions) {
      let steps: SolutionStep[] = [];
      for (const step of solution.steps) {
        if (step.transitions.length > 1) {
          steps.push(step);
        }
        else {
          let skip = false;
          for (const existingStep of steps) {
            if (existingStep.transitions[0] === step.transitions[0]) {
              skip = true;
              break;
            }
          }
          if (!skip) {
            steps.push(step);
          }
        }
      }
      solution.steps = steps;
    }
    for (const solution of solutions) {

      for (const step of solution.steps) {
        let transitions: string[] = [];
        for (const transition of step.transitions) {
          let lastTransition = transitions.length > 0 ? transitions[transitions.length - 1] : null;
          if (lastTransition !== transition) {
            transitions.push(transition);
          }
        }
        step.transitions = transitions;
      }
    }
  }
}



const getters: GetterTree<ProblemState, RootState> = {

  solutionsStepsCount(state): number | undefined {
    return getSolutionStepCount(state);
  }

};

function getSolutionStepCount(state: ProblemState): number | undefined {
  if (state.problem) {

    return state.problem.solutions.reduce<number>(
      (
        previousValue: number,
        currentValue: Solution,
        index: number,
        ar: Solution[]
      ) => {
        if (previousValue > currentValue.steps.length) {
          return currentValue.steps.length;
        } else {
          return previousValue;
        }
      },
      Number.MAX_VALUE
    );
  }
}

export const problemsModule: Module<ProblemState, RootState> = {
  namespaced: true,
  mutations,
  actions,
  state: {
    problem: undefined,
    history: [],
    studentSolution: undefined

  },
  getters
};
